import React, { useState, useEffect } from 'react';
import { useUser } from '../store/UserContext';
import { useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  AcademicCapIcon, 
  ChatBubbleLeftRightIcon, 
  BookmarkIcon,
  BellIcon,
  CogIcon,
  ArrowRightIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    forumPosts: 0,
    savedItems: 0,
    lastActive: 'Today'
  });

  // Mock data - replace with actual API calls
  useEffect(() => {
    // TODO: Fetch user stats from backend
    setStats({
      forumPosts: 5,
      savedItems: 12,
      lastActive: '2 hours ago'
    });
  }, []);

  const quickActions = [
    {
      title: 'CET Zone',
      description: 'Access CET exam information',
      icon: AcademicCapIcon,
      color: 'bg-blue-500',
      path: '/cetzone'
    },
    {
      title: 'CAP Zone', 
      description: 'Explore CAP exam resources',
      icon: ChartBarIcon,
      color: 'bg-green-500',
      path: '/capzone'
    },
    {
      title: 'Community',
      description: 'Join discussions',
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-purple-500',
      path: '/community'
    },
    {
      title: 'Saved Items',
      description: 'View your bookmarks',
      icon: BookmarkIcon,
      color: 'bg-yellow-500',
      path: '/saved'
    }
  ];

  const recentActivity = [
    {
      type: 'forum_post',
      title: 'Posted in CET Discussion',
      time: '2 hours ago',
      description: 'Shared insights about CET preparation'
    },
    {
      type: 'saved_item',
      title: 'Saved CAP Cutoff Data',
      time: '1 day ago',
      description: 'Bookmarked important cutoff information'
    },
    {
      type: 'login',
      title: 'Logged in',
      time: '2 days ago',
      description: 'Accessed your account'
    }
  ];

  const getActivityIcon = (type) => {
    switch(type) {
      case 'forum_post':
        return <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-500" />;
      case 'saved_item':
        return <BookmarkIcon className="w-5 h-5 text-yellow-500" />;
      case 'login':
        return <UserIcon className="w-5 h-5 text-green-500" />;
      default:
        return <DocumentTextIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please Log In</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to access your dashboard.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600">Welcome back, {user.email}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <BellIcon className="w-6 h-6" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <CogIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile & Stats */}
          <div className="lg:col-span-1 space-y-6">
            {/* User Profile Card */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{user.email}</h3>
                  <p className="text-gray-600">Member since {new Date().getFullYear()}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <button className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Edit Profile
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Forum Posts</span>
                  <span className="font-semibold text-gray-900">{stats.forumPosts}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Saved Items</span>
                  <span className="font-semibold text-gray-900">{stats.savedItems}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Last Active</span>
                  <span className="font-semibold text-gray-900">{stats.lastActive}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Quick Actions & Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(action.path)}
                    className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className={`p-2 rounded-lg ${action.color} text-white mr-4`}>
                      <action.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-medium text-gray-900">{action.title}</h4>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  View All Activity
                </button>
              </div>
            </div>

            {/* Personalized Recommendations */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommended for You</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">CET 2024 Preparation Guide</h4>
                    <p className="text-sm text-gray-600">Based on your interests</p>
                  </div>
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                    View
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">CAP Seat Matrix Updates</h4>
                    <p className="text-sm text-gray-600">New data available</p>
                  </div>
                  <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                    View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
